import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { AlertTriangle, ArrowDownUp, Download, Mail } from 'lucide-react'
import participantsApi from 'api/requests/participants'
import ticketsApi from 'api/requests/tickets'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import Modal from 'components/_UI/Modal'
import Paginate from 'components/_UI/Paginate'
import Radio from 'components/_UI/Radio'
import Text from 'components/_UI/Text'
import ToastContainer from 'components/_UI/ToastsContainer'
import DynamicProfileImage from 'components/commons/DynamicProfileImage'
import NotResultDashBoard from 'components/commons/NotResultDashBoard'
import { useEvent } from 'context/events/Event'
import downloadBlob from 'helpers/downloadBlob'
import useToast from 'hooks/useToast'
import { Column } from 'styled/alignment/Column'
import { Row } from 'styled/alignment/Row'
import { FullyCentered } from 'styled/alignment/Center'
import breakpoints from 'theme/breakpoints'
import colors from 'theme/colors'
import { Link } from 'react-router-dom'
import date from 'helpers/date'
import ParticipantsFilters from './ParticipantsFilters'
import CheckinProgress from './CheckinProgress'
import ParticipantRow from './ParticipantRow'
import usePendingCheckin from './usePendingCheckin'
import { formatInt, formatDateTime, statusOf, PARTICIPANT_STATUS } from './participantTokens'

const DOWNLOAD_FORMATS = [
    { name: 'xls', label: 'xls (Planilhas Excel)' },
    { name: 'csv', label: 'csv (Planilhas de dados - formato simples)' },
]

const SORTS = [
    { id: 'recent', label: 'Mais recentes' },
    { id: 'name', label: 'Nome' },
    { id: 'status', label: 'Status' },
]

const STATUS_ORDER = {
    [PARTICIPANT_STATUS.ACTIVE]: 0,
    [PARTICIPANT_STATUS.USED]: 1,
    [PARTICIPANT_STATUS.REFUNDED]: 2,
}

export default function ParticipantsList({ data }) {
    const { event } = useEvent();
    const { toast, toasts } = useToast();

    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadLoader, setDownloadLoader] = useState(false);
    const [meta, setMeta] = useState({ perPage: null, lastPage: null, currentPage: 1, total: 0 });
    const [progress, setProgress] = useState(null);
    const [appliedFilters, setAppliedFilters] = useState({});
    const [detail, setDetail] = useState(null);
    const [downloadModal, setDownloadModal] = useState(false);
    const [selectedFormat, setSelectedFormat] = useState(DOWNLOAD_FORMATS[0].name);
    const [sort, setSort] = useState('recent');

    const slug = data;

    const getParticipants = useCallback(
        async (page = 1, params = {}) => {
            if (!slug) return;

            setLoading(true);

            try {
                const { data: response } = await participantsApi.list(slug, page, params);

                setParticipants(response?.data || []);
                setMeta({
                    perPage: response?.meta?.per_page,
                    lastPage: response?.meta?.last_page,
                    currentPage: response?.meta?.current_page,
                    total: response?.meta?.total || 0,
                });

                // O progresso descreve o evento inteiro, não a página filtrada —
                // por isso só é substituído quando a API realmente devolve um.
                if (response?.progress) setProgress(response.progress);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
        [slug]
    );

    const commitCheckin = useCallback(
        async (participant) => {
            await ticketsApi.checkIn({ code: participant.code });
            await getParticipants(meta.currentPage, appliedFilters);
        },
        [getParticipants, meta.currentPage, appliedFilters]
    );

    const handleCheckinError = useCallback(
        (participant) => {
            toast.error(
                `Não foi possível validar ${participant?.name || 'o participante'}.`,
                { position: 'top-center' }
            );
            getParticipants(meta.currentPage, appliedFilters);
        },
        [toast, getParticipants, meta.currentPage, appliedFilters]
    );

    const { pending, schedule, cancel } = usePendingCheckin({
        onCommit: commitCheckin,
        onError: handleCheckinError,
    });

    useEffect(() => {
        getParticipants(1, appliedFilters);
    }, [slug]);

    const onApplyFilter = useCallback(
        (filters) => {
            setAppliedFilters(filters);
            getParticipants(1, filters);
        },
        [getParticipants]
    );

    const sortedParticipants = useMemo(() => {
        return [...participants].sort((a, b) => {
            if (sort === 'name') return (a?.name || '').localeCompare(b?.name || '', 'pt-BR');
            if (sort === 'status') return STATUS_ORDER[statusOf(a)] - STATUS_ORDER[statusOf(b)];
            return new Date(b?.created_at) - new Date(a?.created_at);
        });
    }, [participants, sort]);

    const hasActiveFilters = Object.keys(appliedFilters).length > 0;

    const downloadFile = async () => {
        setDownloadLoader(true);

        try {
            /* Os filtros são enviados para o export, mas o endpoint hoje só
               considera event_id — verificado comparando a resposta com e sem
               parâmetros. Enquanto o backend não os aplicar, o aviso no modal
               mantém a UI honesta sobre o que o arquivo contém. */
            const { data: file } = await participantsApi.download(event?.id, appliedFilters);
            const eventDate = event?.dates?.[0]?.date;
            const fileName = `Participantes_${event?.name}${eventDate ? `_${date.format(eventDate)}` : ''}.${selectedFormat}`;

            downloadBlob(file, fileName, selectedFormat);
            setDownloadModal(false);
            toast.success('Arquivo baixado com sucesso!', { position: 'top-center' });
        } catch (error) {
            toast.error('Ocorreu um erro ao baixar o arquivo.', { position: 'top-center' });
        } finally {
            setDownloadLoader(false);
        }
    };

    return (
        <Container>
            <ToastContainer toasts={toasts} />

            <Header>
                <Column width='auto'>
                    <Title>Participantes</Title>
                    <Subtitle>
                        {loading
                            ? 'Carregando...'
                            : `${formatInt(meta.total)} ${meta.total === 1 ? 'registro' : 'registros'}${hasActiveFilters ? ' com os filtros atuais' : ''}`}
                    </Subtitle>
                </Column>

                <DownloadWrap>
                    <Button
                        variant={downloadLoader ? 'disabled' : 'featured'}
                        category='squired'
                        onClick={() => setDownloadModal(true)}
                    >
                        <Download color={colors.neutralLight} size={16} />
                        <Text color={colors.neutralLight} paddingBottom={0} marginLeft={'6px'}>
                            Baixar lista
                        </Text>
                    </Button>
                </DownloadWrap>
            </Header>

            {/* Sempre visível: descreve o evento, não o resultado do filtro. */}
            <CheckinProgress progress={progress} />

            <Panel>
                <ParticipantsFilters onApplyFilter={onApplyFilter} />

                <ListHead>
                    <ListCount>
                        {loading
                            ? ' '
                            : `Mostrando ${formatInt(sortedParticipants.length)} de ${formatInt(meta.total)}`}
                    </ListCount>

                    {sortedParticipants.length > 1 && (
                        <SortGroup role='group' aria-label='Ordenar participantes'>
                            <SortIcon aria-hidden='true'>
                                <ArrowDownUp size={13} strokeWidth={2.2} />
                            </SortIcon>
                            {SORTS.map((option) => (
                                <SortButton
                                    key={option.id}
                                    type='button'
                                    $active={sort === option.id}
                                    aria-pressed={sort === option.id}
                                    onClick={() => setSort(option.id)}
                                >
                                    {option.label}
                                </SortButton>
                            ))}
                        </SortGroup>
                    )}
                </ListHead>

                {loading ? (
                    <ContentLoading>
                        <Loader />
                    </ContentLoading>
                ) : sortedParticipants.length > 0 ? (
                    <>
                        <List>
                            {sortedParticipants.map((participant) => (
                                <ParticipantRow
                                    key={participant.code}
                                    participant={participant}
                                    pendingUndo={Boolean(pending[participant.code])}
                                    undoSecondsLeft={pending[participant.code]?.secondsLeft || 0}
                                    onCheckIn={schedule}
                                    onUndo={cancel}
                                    onDetail={setDetail}
                                />
                            ))}
                        </List>

                        {meta.lastPage > 1 && (
                            <Paginate
                                perPage={meta.perPage}
                                totalItems={meta.total}
                                lastPage={meta.lastPage}
                                firstPage={1}
                                currentPage={meta.currentPage}
                                marginTop={'24px'}
                                onPaginate={(number) => getParticipants(number, appliedFilters)}
                            />
                        )}
                    </>
                ) : (
                    <EmptyWrap>
                        <NotResultDashBoard
                            text={
                                <Text name='highlight'>
                                    {hasActiveFilters
                                        ? 'Nenhum participante corresponde aos filtros aplicados.'
                                        : 'Não foram encontrados participantes no seu evento até o momento.'}
                                </Text>
                            }
                            ilustration={'withoutParticipants'}
                            marginTop={'15px'}
                        />
                    </EmptyWrap>
                )}
            </Panel>

            {/* ───────────────────  Detalhe do participante  ─────────────────── */}
            <Modal size='large' isOpen={!!detail} onClose={() => setDetail(null)} childrenOverflowY='scroll'>
                <ModalWrapper>
                    <DetailHead>
                        <DynamicProfileImage
                            img={detail?.avatar}
                            word={detail?.name}
                            size={'72px'}
                            bgColor={colors.secondaryPurple}
                        />
                        <Column>
                            <DetailName>{detail?.name}</DetailName>
                            <DetailEmail>
                                <Mail size={15} color={colors.night} aria-hidden='true' />
                                {detail?.email}
                            </DetailEmail>
                        </Column>
                    </DetailHead>

                    <DetailGrid>
                        <DetailField label='Código' value={detail?.code} mono />
                        <DetailField
                            label='Tipo'
                            value={detail?.type === 'ticket' ? 'Ingresso' : 'Produto'}
                        />
                        <DetailField label='Item' value={detail?.product} />
                        <DetailField label='Data da compra' value={formatDateTime(detail?.created_at)} />
                        <DetailField label='Check-in' value={detail?.used ? 'Realizado' : 'Não realizado'} />
                        <DetailField label='Código promocional' value={detail?.coupon || '—'} />
                    </DetailGrid>

                    {detail?.custom_fields && Object.keys(detail.custom_fields).length > 0 && (
                        <CustomFields>
                            <CustomFieldsTitle>Campos adicionais</CustomFieldsTitle>
                            {Object.entries(detail.custom_fields).map(([key, value]) => (
                                <CustomField key={key}>
                                    <CustomFieldLabel>{key}</CustomFieldLabel>
                                    {typeof value === 'string' &&
                                    (value.startsWith('https://') || value.startsWith('www')) ? (
                                        <AditionalLinkField
                                            to={value.startsWith('www') ? `https://${value}` : value}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            abrir link
                                        </AditionalLinkField>
                                    ) : (
                                        <CustomFieldValue>
                                            {Array.isArray(value) ? value.join(', ') : value}
                                        </CustomFieldValue>
                                    )}
                                </CustomField>
                            ))}
                        </CustomFields>
                    )}
                </ModalWrapper>
            </Modal>

            {/* ───────────────────  Download  ─────────────────── */}
            {!!downloadModal && (
                <Modal isOpen={downloadModal} onClose={() => setDownloadModal(false)}>
                    <Text type='bold'>Selecione o formato que deseja baixar a lista</Text>

                    {DOWNLOAD_FORMATS.map((el) => (
                        <Radio
                            key={el.name}
                            label={el.label}
                            selected={el.name === selectedFormat}
                            onSelect={() => setSelectedFormat(el.name)}
                        />
                    ))}

                    {hasActiveFilters && (
                        <FilterWarning>
                            <AlertTriangle size={15} strokeWidth={2.2} aria-hidden='true' />
                            <span>
                                O arquivo conterá <strong>todos os participantes</strong> do evento.
                                Os filtros aplicados na tela não são considerados na exportação.
                            </span>
                        </FilterWarning>
                    )}

                    {!downloadLoader ? (
                        <Button marginTop={'14px'} variant='featured' onClick={downloadFile}>
                            Baixar lista
                        </Button>
                    ) : (
                        <FullyCentered marginTop='14px'>
                            <Loader />
                        </FullyCentered>
                    )}
                </Modal>
            )}
        </Container>
    );
}

function DetailField({ label, value, mono }) {
    return (
        <Field>
            <FieldLabel>{label}</FieldLabel>
            <FieldValue $mono={mono}>{value || '—'}</FieldValue>
        </Field>
    );
}

/* ─────────────────────────────  Estilos  ───────────────────────────── */

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 20px;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const Title = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const Subtitle = styled.p`
    margin: 2px 0 0;
    font-size: 12px;
    color: ${colors.night};
`;

const DownloadWrap = styled.div`
    width: 100%;

    @media screen and (min-width: ${breakpoints.md}) {
        width: 152px;
        flex-shrink: 0;
    }
`;

const Panel = styled.section`
    padding: 18px 20px 20px;
    border-radius: 16px;
    background: ${colors.neutralLight};
    border: 1px solid rgba(16, 24, 40, 0.1);
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
`;

const ListHead = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin: 18px 0 8px;
    padding-top: 16px;
    border-top: 1px solid rgba(16, 24, 40, 0.09);

    @media screen and (min-width: ${breakpoints.smd}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const ListCount = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.night};
`;

const SortGroup = styled.div`
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 3px;
    border-radius: 10px;
    background: ${colors.neutralBackground};
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const SortIcon = styled.span`
    display: inline-flex;
    padding: 0 6px 0 8px;
    color: ${colors.night};
`;

const SortButton = styled.button`
    padding: 6px 11px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: ${({ $active }) => ($active ? 700 : 500)};
    white-space: nowrap;
    cursor: pointer;
    background: ${({ $active }) => ($active ? colors.neutralLight : 'transparent')};
    color: ${({ $active }) => ($active ? colors.nightDark : colors.night)};
    box-shadow: ${({ $active }) => ($active ? '0 1px 4px rgba(0,0,0,0.1)' : 'none')};

    &:focus-visible {
        outline: 2px solid ${colors.secondaryPurple};
        outline-offset: 2px;
    }
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const ContentLoading = styled(Column)`
    height: 220px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const EmptyWrap = styled.div`
    & > div {
        flex-direction: column;
    }

    & > div > picture {
        width: 45%;
    }

    & > div > div {
        width: 100%;
    }
`;

/* ── Modal de detalhe ── */

const ModalWrapper = styled(Column)`
    padding: 6px 10px 10px;
    align-items: stretch;
`;

const DetailHead = styled(Row)`
    align-items: center;
    gap: 14px;
    padding-bottom: 18px;
    margin-bottom: 18px;
    border-bottom: 1px solid rgba(16, 24, 40, 0.09);
`;

const DetailName = styled.p`
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    text-align: left;
    color: ${colors.nightDark};
`;

const DetailEmail = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 4px 0 0;
    font-size: 13px;
    text-align: left;
    color: ${colors.night};
    word-break: break-all;
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;

    @media screen and (min-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr 1fr;
    }
`;

/* O Modal centraliza o texto por padrão; campos de dado precisam ficar
   alinhados à esquerda para serem varridos com o olho. */
const Field = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
    text-align: left;
`;

const FieldLabel = styled.span`
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: ${colors.night};
`;

const FieldValue = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    word-break: break-word;
    ${({ $mono }) => $mono && `
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        letter-spacing: 0.4px;
    `}
`;

const CustomFields = styled.div`
    margin-top: 20px;
    padding-top: 18px;
    border-top: 1px solid rgba(16, 24, 40, 0.09);
`;

const CustomFieldsTitle = styled.p`
    margin: 0 0 10px;
    font-size: 13px;
    font-weight: 700;
    text-align: left;
    color: ${colors.nightDark};
`;

const CustomField = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 6px;
`;

const CustomFieldLabel = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${colors.nightDark};

    &::after {
        content: ':';
    }
`;

const CustomFieldValue = styled.span`
    font-size: 13px;
    color: ${colors.night};
    word-break: break-word;
`;

const AditionalLinkField = styled(Link)`
    font-size: 13px;
    color: ${colors.primary};
`;

const FilterWarning = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 14px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #fff8e6;
    border: 1px solid #f3dfae;
    font-size: 12px;
    line-height: 1.45;
    text-align: left;
    color: #7a5300;
`;
